import type { FC } from 'react';
import Button from '@components/Button';
import { Show } from '@components/Show';
import { MODAL_Z_INDEX } from '@constants/z-indices';
import { useSchemaStore } from '@ContractBuilder/store';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { ExclamationIcon, XIcon } from '@heroicons/react/outline';
import clsx from 'clsx';

import type { ValidateRequiredDatapointsResult } from '../utils/validate-required-datapoints';

interface MissingMandatoryDatapointsModalProps {
  isOpen: boolean;
  missingDatapoints: ValidateRequiredDatapointsResult;
  onCancel: () => void;
  onConfirm: () => void;
}

export const MissingMandatoryDatapointsModal: FC<MissingMandatoryDatapointsModalProps> = ({
  isOpen,
  missingDatapoints,
  onCancel,
  onConfirm,
}) => {
  const getFieldTitle = (fieldId: string): string => {
    return useSchemaStore.getState().findField(fieldId)?.title ?? fieldId;
  };

  const { list, hasVariations } = missingDatapoints;

  return (
    <Show when={isOpen}>
      <Dialog
        open={isOpen}
        as="div"
        className={clsx('fixed inset-0 overflow-y-auto', MODAL_Z_INDEX)}
        data-testid="missing-mandatory-datapoints-modal"
        onClose={onCancel}
      >
        <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          <div className="bg-info-500/75 fixed inset-0 transition-opacity" />

          <DialogPanel
            transition
            className="flex h-screen items-center justify-center duration-300 ease-out data-closed:transform-[scale(95%)] data-closed:opacity-0"
          >
            <div className="relative inline-block transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6 sm:align-middle">
              <div className="absolute top-0 right-0 hidden pt-4 pr-4 sm:block">
                <button
                  type="button"
                  className="text-info-400 hover:text-info-500 rounded-md bg-white focus:ring-2 focus:ring-blue-500/50 focus:ring-offset-2 focus:outline-hidden"
                  onClick={onCancel}
                >
                  <span className="sr-only">Cancel</span>
                  <XIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
              <div className="sm:flex sm:items-start">
                <div className="bg-error-100 mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10">
                  <ExclamationIcon className="text-error-600 h-6 w-6" aria-hidden="true" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <DialogTitle as="h3" className="text-info-900 text-lg leading-6 font-medium">
                    You have missing mandatory data points
                  </DialogTitle>
                  <div className="text-info-500 mt-2 text-sm leading-5 font-normal">
                    <p className="mb-3">These mandatory data points are missing from this block:</p>
                    <ul className="mb-3 list-disc pl-4">
                      {!hasVariations
                        ? list?.length &&
                          list[0]?.datapoints?.map((dp: string) => <li key={dp}>{getFieldTitle(dp)}</li>)
                        : list!.map((variations) => (
                            <>
                              <li className="font-bold">
                                {variations?.name}
                                <ul key={variations?.name} className="mb-3 pl-4 font-normal">
                                  {variations?.datapoints?.map((dp: string) => (
                                    <li key={dp}>{getFieldTitle(dp)}</li>
                                  ))}
                                </ul>
                              </li>
                            </>
                          ))}
                    </ul>
                    <p>Please ensure all of these are present within the block content.</p>
                  </div>
                </div>
              </div>
              <div className="mt-6 items-center justify-end gap-x-2.5 sm:flex">
                <Button className="h-auto px-4 py-2" onClick={onConfirm} kind="primary" size="sm">
                  Ok
                </Button>
              </div>
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    </Show>
  );
};
